import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BaseFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
}

export function TextField({
  id,
  type = 'text',
  inputMode,
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus,
  required,
  disabled,
  hint,
}: BaseFieldProps & {
  type?: 'text' | 'email';
  inputMode?: 'text' | 'email';
}) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div>
      <label htmlFor={inputId} className="block text-[12.5px] font-medium text-ink-700 mb-1.5">
        {label}
        {required && <span className="text-citation-600 ml-0.5">*</span>}
      </label>
      <input
        id={inputId}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        className="w-full rounded-lg bg-white border border-ink-200 px-3 py-2.5 text-[14.5px] text-ink-900 placeholder:text-ink-400 hover:border-ink-300 focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-500/15 transition disabled:opacity-50"
      />
      {hint && <p className="mt-1 text-[11.5px] text-ink-500">{hint}</p>}
    </div>
  );
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  required,
  disabled,
  showStrength,
}: BaseFieldProps & { showStrength?: boolean }) {
  const { t } = useTranslation();
  const auto = useId();
  const inputId = id ?? auto;
  const [reveal, setReveal] = useState(false);
  const strength = showStrength ? scorePassword(value) : null;

  return (
    <div>
      <label htmlFor={inputId} className="block text-[12.5px] font-medium text-ink-700 mb-1.5">
        {label}
        {required && <span className="text-citation-600 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <input
          id={inputId}
          type={reveal ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          className="w-full rounded-lg bg-white border border-ink-200 px-3 py-2.5 pr-10 text-[14.5px] text-ink-900 hover:border-ink-300 focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-500/15 transition disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          aria-label={reveal
            ? (t('auth.hide_password', { defaultValue: 'Hide password' }) as string)
            : (t('auth.show_password', { defaultValue: 'Show password' }) as string)}
          className="absolute inset-y-0 right-2 inline-flex items-center justify-center w-8 text-ink-400 hover:text-ink-700"
        >
          {reveal ? <EyeOff /> : <Eye />}
        </button>
      </div>
      {showStrength && (
        <StrengthMeter score={strength!} value={value} />
      )}
    </div>
  );
}

function StrengthMeter({ score, value }: { score: PasswordScore; value: string }) {
  const { t } = useTranslation();
  // 0..4 buckets (very weak, weak, fair, good, strong)
  const labels = [
    t('auth.strength.very_weak', { defaultValue: 'Very weak' }),
    t('auth.strength.weak', { defaultValue: 'Weak' }),
    t('auth.strength.fair', { defaultValue: 'Fair' }),
    t('auth.strength.good', { defaultValue: 'Good' }),
    t('auth.strength.strong', { defaultValue: 'Strong' }),
  ];
  const colors = ['bg-warn-500', 'bg-warn-500', 'bg-citation-500', 'bg-accent-500', 'bg-accent-600'];
  if (!value) {
    return (
      <p className="mt-1.5 text-[11.5px] text-ink-500">
        {t('auth.password_hint', { defaultValue: 'At least 8 characters. Longer is stronger.' })}
      </p>
    );
  }
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition ${i <= score.bucket ? colors[score.bucket] : 'bg-ink-100'}`}
          />
        ))}
      </div>
      <p className="mt-1.5 text-[11.5px] text-ink-500">{labels[score.bucket]}</p>
    </div>
  );
}

export function OrDivider({ label }: { label: string }) {
  return (
    <div className="my-5 flex items-center gap-3">
      <div className="flex-1 border-t border-ink-100" />
      <span className="text-[11px] uppercase tracking-[0.18em] text-ink-400 font-mono">{label}</span>
      <div className="flex-1 border-t border-ink-100" />
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-lg border border-warn-500/30 bg-warn-500/[0.06] px-3 py-2 text-[13px] text-warn-600"
    >
      {message}
    </div>
  );
}

export function FormSuccess({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="rounded-lg border border-accent-500/30 bg-accent-500/[0.06] px-3 py-2 text-[13px] text-accent-700"
    >
      {message}
    </div>
  );
}

export function SubmitButton({
  pending,
  children,
  disabled,
}: {
  pending: boolean;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={pending || disabled}
      className="btn-press w-full"
    >
      {pending ? (
        <span className="inline-flex items-center gap-2">
          <Spinner /> <span>…</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
}

function Eye() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.04 12C3.62 6.16 7.62 3 12 3s8.38 3.16 9.96 9c-1.58 5.84-5.58 9-9.96 9s-8.38-3.16-9.96-9z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOff() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.58 10.58a3 3 0 004.24 4.24M9.88 5.09A10.94 10.94 0 0112 5c4.38 0 8.38 3.16 9.96 9-.46 1.71-1.16 3.21-2.04 4.45M6.61 6.61C4.6 7.85 3.07 9.74 2.04 12c1.58 5.84 5.58 9 9.96 9 1.66 0 3.22-.5 4.61-1.39" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  );
}

// ---------- password scoring ----------

export interface PasswordScore {
  bucket: 0 | 1 | 2 | 3 | 4;
  signals: string[];
}

export function scorePassword(pw: string): PasswordScore {
  const signals: string[] = [];
  if (!pw) return { bucket: 0, signals };

  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z\d]/].filter((re) => re.test(pw)).length;
  score += classes - 1; // each extra class adds 1
  if (/(.)\1{2,}/.test(pw)) score--; // repeated chars penalty
  if (/123|abc|password|qwerty/i.test(pw)) score -= 2;
  const bucket = Math.max(0, Math.min(4, score)) as 0 | 1 | 2 | 3 | 4;

  if (pw.length < 8) signals.push('length');
  if (classes < 2) signals.push('variety');
  return { bucket, signals };
}
